<template>
	<CookieTemplateLayout
		:header-title="'Consent Banners' | useLabels('Consent Banners')"
		:header-caption="'Add, edit and manage consent banners' | useLabels('Add, edit and manage consent banners')"
	>
		<template #content>
			<FilterBar
				:search-query.sync="cookieBannerSearchQuery"
				search-query-label="Consent Banner Name"
				show-meta-data-filters
				:selected-brand-id.sync="selectedAdminPortalBrandId"
				:selected-meta-data-key-id.sync="selectedMetaDataKey"
				:meta-data-search-query.sync="metaDataValueSearchQuery"
				@persistSearchQuery="changeCookieBannerSearchQuery"
				@persistSelectedBrandId="changeAdminPortalBrandFilter"
				@persistMetaDataSearchQuery="changeMetaDataValueSearchQuery"
				@persistSelectedMetaDataKeyId="changeMetaDataKey"
			>
				<template #action>
					<PrimaryActionButton
						v-if="userCanCreateUpdate"
						@click="onCreateClick()"
					>
						<v-icon left>
							mdi-plus
						</v-icon>
						Create
					</PrimaryActionButton>
				</template>
			</FilterBar>
			<SectionCard>
				<template #title>
					Consent Banners
				</template>
				<template #body>
					<DataTable
						:items="filteredBanners"
						:headers="tableHeaders"
						sort-by="cookieBannerId"
						sort-desc
						@click:row="onEditClick"
					>
						<template #item.action="{ item }">
							<v-menu
								offset-y
								left
							>
								<template #activator="{ on, attrs }">
									<v-btn
										icon
										small
										v-bind="attrs"
										v-on="on"
										@click.stop
									>
										<v-icon>
											mdi-dots-horizontal
										</v-icon>
									</v-btn>
								</template>
								<v-list dense>
									<v-list-item
										v-if="userCanCreateUpdate && userHasBrand(item.brandId)"
										@click="onEditClick(item)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Edit
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-else
										@click="onEditClick(item)"
									>
										<v-list-item-content>
											<v-list-item-title>
												View
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="userCanCreateUpdate && userHasBrand(item.brandId)"
										@click.stop.prevent="onCloneClick(item)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Clone
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="userCanDelete && userHasBrand(item.brandId)"
										@click.stop.prevent="cookieBannerToForceReconsent = item"
									>
										<v-list-item-content>
											<v-list-item-title>
												Force Reconsent
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="userCanDelete && userHasBrand(item.brandId)"
										@click.stop.prevent="cookieBannerToDelete = item"
									>
										<v-list-item-content>
											<v-list-item-title>
												Delete
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
					</DataTable>
				</template>
			</SectionCard>

			<ConfirmDeleteModal
				v-if="cookieBannerToDelete && userCanDelete"
				:entity-name="cookieBannerToDelete.name"
				entity-type="Consent Bannner"
				@close="cookieBannerToDelete = null"
				@delete="deleteCookieBanner"
			/>
			<ConfirmationModal
				v-if="cookieBannerToForceReconsent && userCanCreateUpdate"
				title-text="Consent Information"
				@cancel="cookieBannerToForceReconsent = null"
				@confirm="forceCookieBannerReconsent"
			>
				<template #modal-content>
					Please confirm if you are happy to request new consents for the selected consent banner.
				</template>
			</ConfirmationModal>

			<TemplateSelectorModal
				v-if="showTemplateSelectorModal"
				@close="showTemplateSelectorModal = false"
				@template-selected="onTemplateSelected"
				@custom-selected="onCustomSelected"
			/>
		</template>
	</CookieTemplateLayout>
</template>
<script>
import { mapGetters } from 'vuex'
import CookieTemplateLayout from '../cookie-template-layout.vue'
import FilterBar from '../../../../../../../shared/components/filter-bar.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import ConfirmDeleteModal from '../../../../../../../shared/components/confirm-delete-modal.vue'
import ConfirmationModal from '../../../../../../../shared/components/confirmation-modal.vue'
import TemplateSelectorModal from './template-selector-modal.vue'
import DataTable from '../../../../../../../shared/components/data-table.vue'
import { useLabels } from '../../../../../../../shared/state/admin-portal-navigation.js'
import { MANAGE_COOKIE_BANNER } from '../../../../../router/route-names.js'
import { getCookieBanners, deleteCookieBanner, cookieBannerForceReconsent } from '../../../../../../../shared/utils/api/cookies/cookie-banners.js'
import { showSnackbar } from '../../../../../../../shared/state/snackbar.js'
import { CAN_CREATE_UPDATE_COOKIE_BANNERS, CAN_DELETE_COOKIE_BANNERS } from '../../../../../../../shared/permissions/admin-portal-permissions.js'
import { changeCookieBannerSearchQuery, cookieBannerSearchQuery } from '../../../../../../../shared/state/cookie-banners.js'
import { metaDataValueSearchQuery, selectedMetaDataKey, changeMetaDataKey, changeMetaDataValueSearchQuery } from '../../../../../../../shared/state/meta-data.js'
import { changeAdminPortalBrandFilter, selectedAdminPortalBrandId, userHasBrand } from '../../../../../../../shared/state/brands.js'
export default {
	components: {
		CookieTemplateLayout,
		FilterBar,
		PrimaryActionButton,
		SectionCard,
		DataTable,
		ConfirmDeleteModal,
		ConfirmationModal,
		TemplateSelectorModal
	},
	setup () {
		return {
			userHasBrand,
			changeAdminPortalBrandFilter,
			selectedAdminPortalBrandId,
			changeCookieBannerSearchQuery,
			cookieBannerSearchQuery,
			metaDataValueSearchQuery,
			selectedMetaDataKey,
			changeMetaDataKey,
			changeMetaDataValueSearchQuery
		}
	},
	data () {
		return {
			cookieBanners: [],
			cookieBannerToDelete: null,
			cookieBannerToForceReconsent: null,
			showTemplateSelectorModal: false
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		userCanCreateUpdate () {
			return this.productAreaPermission(CAN_CREATE_UPDATE_COOKIE_BANNERS)
		},
		userCanDelete () {
			return this.productAreaPermission(CAN_DELETE_COOKIE_BANNERS)
		},
		tableHeaders () {
			return [
				{ value: 'cookieBannerId', text: 'ID', width: '6%' },
				{ value: 'brandName', text: useLabels('Brand'), width: '12%' },
				{ value: 'name', text: 'Consent Banner Name', width: '18%' },
				{ value: 'cookieCollectionTemplateName', text: 'Collection Template', width: '16%' },
				{ value: 'cookieTextTemplateName', text: 'Text Template', width: '16%' },
				{ value: 'cookieAppearanceTemplateName', text: 'Appearance Template', width: '16%' },
				{ value: 'createdDate', text: 'Created Date', width: '16%' },
				{ value: 'action', text: 'Action', width: '80px', align: 'center', sortable: false }
			]
		},
		filteredBanners () {
			return this.cookieBanners.filter(({ name, brandId, metaData }) => {
				let check = true
				if (cookieBannerSearchQuery.value) check = name.toLowerCase().includes(cookieBannerSearchQuery.value.toLowerCase())
				if (selectedAdminPortalBrandId.value !== null) check = check && brandId === selectedAdminPortalBrandId.value
				if (this.selectedMetaDataKey !== null) check = check && metaData.some(meta => meta.metaDataKeyId === this.selectedMetaDataKey)
				if (this.metaDataValueSearchQuery) check = check && metaData.some(meta => meta.metaDataValue.toLowerCase().includes(this.metaDataValueSearchQuery.toLowerCase()))
				return check
			}).map(banner => ({
				...banner
			}))
		}
	},
	created () {
		this.getCookieBanners()
	},
	methods: {
		async getCookieBanners () {
			const { cookieBanners } = await getCookieBanners()
			this.cookieBanners = cookieBanners
		},
		onCreateClick () {
			this.showTemplateSelectorModal = true
		},
		onEditClick (item) {
			this.$router.push({
				name: MANAGE_COOKIE_BANNER,
				params: {
					cookieBannerToEdit: item
				}
			})
		},
		onCloneClick (item) {
			const cookieBannerToEdit = {
				...item,
				name: `${item.name} (clone)`
			}
			this.$router.push({
				name: MANAGE_COOKIE_BANNER,
				params: {
					cookieBannerToEdit: cookieBannerToEdit,
					cloneMode: true
				}
			})
		},
		async forceCookieBannerReconsent () {
			await cookieBannerForceReconsent(this.cookieBannerToForceReconsent.cookieBannerId)
			showSnackbar(`Consent Banner ${this.cookieBannerToForceReconsent.name} has been forced to reconsent`)
			this.cookieBannerToForceReconsent = null
		},
		async deleteCookieBanner () {
			await deleteCookieBanner(this.cookieBannerToDelete.cookieBannerId)
			showSnackbar(`You have removed this Consent Banner`)
			this.cookieBannerToDelete = null
			this.getCookieBanners()
		},
		onTemplateSelected (templateId) {
			// Navigate to template management page with selected template
			this.$router.push({
				name: MANAGE_COOKIE_BANNER,
				params: {
					useTemplate: templateId !== null,
					templateType: templateId
				}
			})
		},
		onCustomSelected () {
			// Navigate to custom banner creation
			this.$router.push({
				name: MANAGE_COOKIE_BANNER
			})
		}
	}
}
</script>
